import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import SearchInput from 'assets/components/inputs/search/SearchInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import { compact, differenceBy, filter, flatten, sortBy, uniqBy } from 'lodash';
import Permission from 'models/core/permission/Model.Permission';
import Role from 'models/core/role/Model.Role';
import { useMemo, useState } from 'react';

type UserEditRolesProps = {
  roles: Role[];
  permissions: Permission[];
  isLoading: boolean;
};
export default function UserEditAdditionalPermissions({ roles, permissions, isLoading }: UserEditRolesProps) {
  const { lang } = getLocales();
  const { form } = useModalForm<Api.Core.User.Save.IRequest>();

  const [showSelectedPermissionsOnly, setShowSelectedPermissionsOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<number>();
  const [appFilter, setAppFilter] = useState<number>();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPermissions = useMemo(() => {
    function validateQuery(it: Permission) {
      return new RegExp(searchQuery.toLowerCase()).test((it.name + ' ' + it.description + ' ').toLowerCase());
    }
    return sortBy(
      filter(
        compact(permissions),
        (it) =>
          (!searchQuery ? true : validateQuery(it)) &&
          (!appFilter ? true : it.application.id === appFilter) &&
          (!categoryFilter ? true : it.category.id === categoryFilter)
      ),
      (it) => it.application.name,
      (it) => it.name
    );
  }, [permissions, searchQuery, appFilter, categoryFilter]);

  const filterApps = useMemo(
    () =>
      sortBy(
        uniqBy(compact(flatten(permissions?.map((it) => it.application))), (it) => it.id),
        (it) => it.name
      ),
    [permissions]
  );
  const filterCategories = useMemo(
    () =>
      sortBy(
        uniqBy(compact(flatten(permissions?.map((it) => it.category))), (it) => it.id),
        (it) => it.name
      ),
    [permissions]
  );

  const nonSelectedPermissions = differenceBy(
    filteredPermissions,
    flatten(form.data?.roles.map((it) => roles?.find((r) => r.id == it.id)?.permissions)).filter((it) => it),
    (permission) => permission.id
  );
  const permissionList = showSelectedPermissionsOnly
    ? nonSelectedPermissions?.filter((permission) =>
        form.data.additionalPermissions?.find((it) => it.permission.id === permission.id)
      )
    : nonSelectedPermissions;
  const areAllSelected = form.data.additionalPermissions?.length === permissionList.length;
  const areNoneSelected = form.data.additionalPermissions?.length === 0;

  return (
    <table className={ThemeTableSimple}>
      <thead>
        <tr>
          <th>
            <Button
              class={{ button: ThemeButtonCircle }}
              media="fas-tasks"
              onClick={() => setShowSelectedPermissionsOnly((it) => !it)}
              isLoading={isLoading}
              active={showSelectedPermissionsOnly}
              htmlElementProps={{ title: lang.showOnlySelected }}
            />
          </th>
          <th colSpan={2}>
            <SearchInput onSearch={(q) => setSearchQuery(q)} />
          </th>
          <th>
            <SelectInput
              name="filter"
              placeholder={lang.application}
              data={filterApps.map((item) => item.displayInfo)}
              value={appFilter}
              disabled={!filterApps.length}
              onChange={({ filter }) => setAppFilter(filter)}
              style={{ marginTop: 0 }}
              allowClear
            />
          </th>
          <th>
            <SelectInput
              name="filter"
              placeholder={lang.category}
              data={filterCategories.map((item) => item.displayInfo)}
              value={categoryFilter}
              disabled={!filterCategories.length}
              onChange={({ filter }) => setCategoryFilter(filter)}
              style={{ marginTop: 0 }}
              allowClear
            />
          </th>
        </tr>
        <tr>
          <th>
            <Button
              class={{ button: ThemeButtonCircle }}
              media={areAllSelected ? 'fas-check-square' : areNoneSelected ? 'far-square' : 'fas-minus-square'}
              disabled={!permissions.length}
              onClick={async () => {
                form.update({
                  additionalPermissions: areAllSelected
                    ? []
                    : compact(permissionList).map((permission) => ({ permission: { id: permission.id } })),
                });
              }}
              isLoading={isLoading}
              active={!areNoneSelected}
              htmlElementProps={{ title: lang.selectAll }}
            />
          </th>
          <th>{lang.expiryDate}</th>
          <th>{lang.permission}</th>
          <th>{lang.application}</th>
          <th>{lang.category}</th>
        </tr>
      </thead>
      {permissionList?.length > 0 && (
        <tbody>
          <DataMap
            data={permissionList}
            render={({ data: permission }) => {
              const currentPermission = form.data.additionalPermissions?.find(
                (it) => it.permission.id === permission.id
              );

              return (
                <tr key={permission.id}>
                  <td>
                    <Button
                      class={{ button: ThemeButtonCircle }}
                      htmlElementProps={{
                        title: currentPermission ? lang.unselect : lang.select,
                      }}
                      onClick={() => {
                        if (currentPermission)
                          form.update({
                            additionalPermissions: form.data.additionalPermissions?.filter(
                              (it) => it.permission?.id !== currentPermission?.permission?.id
                            ),
                          });
                        else
                          form.update({
                            additionalPermissions: [
                              ...(form.data.additionalPermissions || []),
                              { permission: { id: permission.id } },
                            ],
                          });
                      }}
                      media={currentPermission ? 'fas-check-square' : 'far-square'}
                    />
                  </td>
                  <td>
                    <DateInput
                      dateTimeFormat={defaultInputDateTimeFormat}
                      onChange={({ expiresAtUtc }) => {
                        if (currentPermission) {
                          currentPermission.expiresAtUtc = expiresAtUtc;
                          form.update({ additionalPermissions: form.data.additionalPermissions });
                        }
                      }}
                      value={currentPermission?.expiresAtUtc}
                      name="expiresAtUtc"
                      placeholder={!currentPermission?.expiresAtUtc ? lang.unlimited : ''}
                      disabled={!currentPermission}
                      icon={<Icon class="fas-calendar-alt" />}
                      style={{ minWidth: '200px' }}
                    />
                  </td>
                  <td>
                    <b>{permission.name}</b>
                    <br />
                    <span>{permission.description}</span>
                  </td>
                  <td>{permission?.application?.name}</td>
                  <td>{permission?.category?.name}</td>
                </tr>
              );
            }}
          />
        </tbody>
      )}
    </table>
  );
}
